/**
 * ProcessBuilderList Component
 *
 * Main list view for processes with search, filter, and actions.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  Copy,
  Pencil,
  Trash2,
  Play,
  Pause,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  AlertCircle,
  Zap,
  FileText,
  RefreshCw,
  Filter
} from 'lucide-react';
import {
  StandardButton,
  StandardModal,
  StandardInput,
  StandardSelect,
  StandardSearchBar,
  StandardCard,
  StandardLoadingState,
  StandardEmptyState,
  useToast
} from '../../standard';
import * as processBuilderStorageRouter from '../../../services/processBuilderStorageRouter';
import { processBuilderStorage } from '../../../services/processBuilderStorage';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { mockProcesses, mockProcessExecutions } from '../../../data/mockProcesses';
import type {
  Process,
  ProcessSummary,
  ProcessBuilderStats,
  ProcessTriggerType
} from '../../../types/processBuilder';

// ============================================
// INTERFACES
// ============================================

interface ProcessBuilderListProps {
  onEditProcess?: (processId: string) => void;
  onViewProcess?: (processId: string) => void;
  onCreateProcess?: () => void;
  // Legacy prop aliases
  onEdit?: (processId: string) => void;
  onViewDetail?: (processId: string) => void;
  onCreate?: () => void;
}

// ============================================
// CONSTANTS
// ============================================

const OBJECT_TYPE_OPTIONS = [
  { value: '', label: 'All Object Types' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Contract', label: 'Contract' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Task', label: 'Task' },
  { value: 'Account', label: 'Account' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'draft', label: 'Draft' }
];

const TRIGGER_TYPE_OPTIONS = [
  { value: '', label: 'All Triggers' },
  { value: 'record_created', label: 'Record Created' },
  { value: 'record_updated', label: 'Record Updated' },
  { value: 'record_created_or_updated', label: 'Created or Updated' },
  { value: 'platform_event', label: 'Platform Event' },
  { value: 'invocable', label: 'Invocable' }
];

const TRIGGER_LABELS: Record<ProcessTriggerType, string> = {
  record_created: 'Created',
  record_updated: 'Updated',
  record_created_or_updated: 'Created/Updated',
  platform_event: 'Platform Event',
  invocable: 'Invocable'
};

// ============================================
// HELPER COMPONENTS
// ============================================

const StatusBadge: React.FC<{ status: string; isActive: boolean }> = ({ status, isActive }) => {
  const getStatusStyles = () => {
    if (status === 'draft') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
    if (isActive) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    }
    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  };

  const getIcon = () => {
    if (status === 'draft') {
      return <FileText className="w-3 h-3 mr-1" />;
    }
    if (isActive) {
      return <CheckCircle className="w-3 h-3 mr-1" />;
    }
    return <XCircle className="w-3 h-3 mr-1" />;
  };

  const getLabel = () => {
    if (status === 'draft') return 'Draft';
    return isActive ? 'Active' : 'Inactive';
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyles()}`}>
      {getIcon()}
      {getLabel()}
    </span>
  );
};

const ObjectTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const colors: Record<string, string> = {
    Lead: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    Opportunity: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    Case: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    Contract: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    Contact: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
    Task: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    Account: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
      {type}
    </span>
  );
};

const TriggerBadge: React.FC<{ type: ProcessTriggerType }> = ({ type }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
    <Zap className="w-3 h-3 mr-1" />
    {TRIGGER_LABELS[type] || type}
  </span>
);

const StatsCard: React.FC<{ stats: ProcessBuilderStats }> = ({ stats }) => (
  <div className="grid grid-cols-4 gap-4 mb-6">
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalProcesses}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">Total Processes</div>
    </div>
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.activeProcesses}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
    </div>
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.totalExecutions}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">Executions</div>
    </div>
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.successRate}%</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export const ProcessBuilderList: React.FC<ProcessBuilderListProps> = ({
  onEditProcess,
  onViewProcess,
  onCreateProcess,
  onEdit,
  onViewDetail,
  onCreate
}) => {
  // Resolve prop names
  const handleEdit = onEditProcess || onEdit || (() => {});
  const handleViewDetail = onViewProcess || onViewDetail || (() => {});
  const handleCreate = onCreateProcess || onCreate || (() => {});
  const { success, error } = useToast();

  // State
  const [processes, setProcesses] = useState<Process[]>([]);
  const [stats, setStats] = useState<ProcessBuilderStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [objectTypeFilter, setObjectTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [triggerTypeFilter, setTriggerTypeFilter] = useState('');
  const [selectedProcesses, setSelectedProcesses] = useState<Set<string>>(new Set());

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState<Process | null>(null);

  // Clone modal state
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [processToClone, setProcessToClone] = useState<Process | null>(null);
  const [cloneName, setCloneName] = useState('');

  // Load data
  useEffect(() => {
    loadProcesses();
  }, []);

  const loadProcesses = async () => {
    setIsLoading(true);
    try {
      if (!USE_GOBODO_API) {
        processBuilderStorage.initializeWithMockData(mockProcesses, mockProcessExecutions);
      }
      const data = await processBuilderStorageRouter.getAllProcessesAsync();
      setProcesses(data);
      const s = await processBuilderStorageRouter.getStatsAsync();
      setStats(s);
    } catch (err) {
      error('Error', 'Failed to load processes');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter processes
  const filteredProcesses = useMemo(() => {
    return processes.filter(process => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = process.name.toLowerCase().includes(query);
        const matchesDescription = process.description.toLowerCase().includes(query);
        const matchesApiName = process.apiName?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription && !matchesApiName) return false;
      }

      // Object type filter
      if (objectTypeFilter && process.objectType !== objectTypeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter) {
        if (statusFilter === 'active' && !process.isActive) return false;
        if (statusFilter === 'inactive' && (process.isActive || process.status === 'draft')) return false;
        if (statusFilter === 'draft' && process.status !== 'draft') return false;
      }

      // Trigger type filter
      if (triggerTypeFilter && process.triggerType !== triggerTypeFilter) {
        return false;
      }

      return true;
    });
  }, [processes, searchQuery, objectTypeFilter, statusFilter, triggerTypeFilter]);

  const handleToggleStatus = async (process: Process) => {
    const updated =
      process.isActive
        ? await processBuilderStorageRouter.deactivateProcessAsync(process.id)
        : await processBuilderStorageRouter.activateProcessAsync(process.id);
    if (updated) {
      setProcesses(prev => prev.map(p => (p.id === process.id ? updated : p)));
      success(updated.isActive ? 'Process Activated' : 'Process Deactivated', `${process.name} is now ${updated.isActive ? 'active' : 'inactive'}`);
      const s = await processBuilderStorageRouter.getStatsAsync();
      setStats(s);
    }
  };

  const handleDeleteClick = (process: Process) => {
    setProcessToDelete(process);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!processToDelete) return;
    const deleted = await processBuilderStorageRouter.deleteProcessAsync(processToDelete.id);
    if (deleted) {
      setProcesses(prev => prev.filter(p => p.id !== processToDelete.id));
      const s = await processBuilderStorageRouter.getStatsAsync();
      setStats(s);
      success('Deleted', `${processToDelete.name} has been deleted`);
    } else {
      error('Error', 'Failed to delete the process');
    }
    setDeleteModalOpen(false);
    setProcessToDelete(null);
  };

  const handleCloneClick = (process: Process) => {
    setProcessToClone(process);
    setCloneName(`${process.name} (Copy)`);
    setCloneModalOpen(true);
  };

  const handleConfirmClone = async () => {
    if (!processToClone || !cloneName.trim()) return;
    const cloned = await processBuilderStorageRouter.cloneProcessAsync({
      sourceProcessId: processToClone.id,
      newName: cloneName.trim(),
      includeScheduledActions: true
    });
    if (cloned) {
      setProcesses(prev => [...prev, cloned]);
      const s = await processBuilderStorageRouter.getStatsAsync();
      setStats(s);
      success('Cloned', `Created ${cloned.name}`);
    } else {
      error('Error', 'Failed to clone the process');
    }
    setCloneModalOpen(false);
    setProcessToClone(null);
    setCloneName('');
  };

  const handleBulkToggle = async (activate: boolean) => {
    for (const id of Array.from(selectedProcesses)) {
      if (activate) await processBuilderStorageRouter.activateProcessAsync(id);
      else await processBuilderStorageRouter.deactivateProcessAsync(id);
    }
    await loadProcesses();
    setSelectedProcesses(new Set());
    success('Updated', `${selectedProcesses.size} processes ${activate ? 'activated' : 'deactivated'}`);
  };

  const toggleSelection = (processId: string) => {
    setSelectedProcesses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(processId)) {
        newSet.delete(processId);
      } else {
        newSet.add(processId);
      }
      return newSet;
    });
  };

  const toggleAllSelection = () => {
    if (selectedProcesses.size === filteredProcesses.length) {
      setSelectedProcesses(new Set());
    } else {
      setSelectedProcesses(new Set(filteredProcesses.map(p => p.id)));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getActionCount = (process: Process) => {
    let count = 0;
    Object.values(process.immediateActions).forEach(actions => {
      count += actions.length;
    });
    return count;
  };

  const getScheduledCount = (process: Process) => {
    let count = 0;
    Object.values(process.scheduledActions).forEach(scheduled => {
      count += scheduled.length;
    });
    return count;
  };

  // Loading state
  if (isLoading) {
    return <StandardLoadingState message="Loading processes..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Process Builder
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Design automated processes with if/then logic for your workflows
          </p>
        </div>
        <div className="flex gap-3">
          <StandardButton
            variant="secondary"
            onClick={loadProcesses}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleCreate}
            leftIcon={<Plus className="w-4 h-4" />}
            data-testid="new-process-builder-btn"
          >
            New Process
          </StandardButton>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && <StatsCard stats={stats} />}

      {/* Dev Mode Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Development Mode
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Showing {processes.length} process{processes.length === 1 ? '' : 'es'} persisted via{' '}
              {USE_GOBODO_API ? 'Gobodo API' : 'processBuilderStorage.ts (localStorage)'}.
              Changes survive page refresh.
            </p>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProcesses.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
              {selectedProcesses.size} process{selectedProcesses.size > 1 ? 'es' : ''} selected
            </div>
            <div className="flex items-center gap-3">
              <StandardButton
                size="sm"
                variant="secondary"
                leftIcon={<Play className="w-4 h-4" />}
                onClick={() => handleBulkToggle(true)}
              >
                Activate
              </StandardButton>
              <StandardButton
                size="sm"
                variant="secondary"
                leftIcon={<Pause className="w-4 h-4" />}
                onClick={() => handleBulkToggle(false)}
              >
                Deactivate
              </StandardButton>
              <StandardButton
                size="sm"
                variant="ghost"
                onClick={() => setSelectedProcesses(new Set())}
              >
                Clear Selection
              </StandardButton>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[250px]">
          <StandardSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search processes..."
          />
        </div>
        <div className="w-[160px]">
          <StandardSelect
            value={objectTypeFilter}
            onChange={(e) => setObjectTypeFilter(e.target.value)}
            options={OBJECT_TYPE_OPTIONS}
          />
        </div>
        <div className="w-[150px]">
          <StandardSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="w-[170px]">
          <StandardSelect
            value={triggerTypeFilter}
            onChange={(e) => setTriggerTypeFilter(e.target.value)}
            options={TRIGGER_TYPE_OPTIONS}
          />
        </div>
      </div>

      {/* Process Table */}
      <StandardCard padding="none">
        {filteredProcesses.length === 0 && !searchQuery && !objectTypeFilter && !statusFilter && !triggerTypeFilter ? (
          <StandardEmptyState
            icon={<GitBranch size={48} />}
            title="No processes"
            message="Get started by creating your first automated process"
            actionLabel="Create Process"
            onAction={handleCreate}
          />
        ) : filteredProcesses.length === 0 ? (
          <StandardEmptyState
            icon={<Filter size={48} />}
            title="No processes found"
            message="No processes match your filters"
          />
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selectedProcesses.size === filteredProcesses.length && filteredProcesses.length > 0}
                      onChange={toggleAllSelection}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Process Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Trigger
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Groups
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Executions
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Modified
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProcesses.map(process => (
                  <tr
                    key={process.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => handleViewDetail(process.id)}
                  >
                    {/* Checkbox */}
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedProcesses.has(process.id)}
                        onChange={() => toggleSelection(process.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>

                    {/* Name & Object Type */}
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {process.name}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <ObjectTypeBadge type={process.objectType} />
                        {process.category && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {process.category}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Trigger Type */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TriggerBadge type={process.triggerType} />
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleToggleStatus(process)}>
                        <StatusBadge status={process.status} isActive={process.isActive} />
                      </button>
                    </td>

                    {/* Criteria Groups */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        {process.criteriaGroups.length}
                      </span>
                    </td>

                    {/* Actions Count */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400" title="Immediate Actions">
                          <Zap size={12} />
                          {getActionCount(process)}
                        </span>
                        {getScheduledCount(process) > 0 && (
                          <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400" title="Scheduled Actions">
                            <Clock size={12} />
                            {getScheduledCount(process)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Executions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title="Successful">
                          <CheckCircle size={12} />
                          {process.successCount || 0}
                        </span>
                        {(process.failureCount || 0) > 0 && (
                          <span className="flex items-center gap-1 text-red-600 dark:text-red-400" title="Failed">
                            <XCircle size={12} />
                            {process.failureCount}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Modified */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(process.lastModifiedDate)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        v{process.currentVersion}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <StandardButton
                          size="xs"
                          variant="ghost"
                          onClick={() => handleViewDetail(process.id)}
                          title="View Details"
                        >
                          <BarChart3 size={14} />
                        </StandardButton>
                        <StandardButton
                          size="xs"
                          variant="ghost"
                          onClick={() => handleEdit(process.id)}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </StandardButton>
                        <StandardButton
                          size="xs"
                          variant="ghost"
                          onClick={() => handleCloneClick(process)}
                          title="Clone"
                        >
                          <Copy size={14} />
                        </StandardButton>
                        <StandardButton
                          size="xs"
                          variant="ghost"
                          onClick={() => handleDeleteClick(process)}
                          title="Delete"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </StandardButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </StandardCard>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <span>
          Showing {filteredProcesses.length} of {processes.length} processes
        </span>
        <span>
          {processes.filter(p => p.isActive).length} active,{' '}
          {processes.filter(p => !p.isActive && p.status !== 'draft').length} inactive,{' '}
          {processes.filter(p => p.status === 'draft').length} draft
        </span>
      </div>

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Process"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Are you sure you want to delete "{processToDelete?.name}"?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone. All execution history will be deleted.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={handleConfirmDelete}
          >
            Delete Process
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>

      {/* Clone Modal */}
      <StandardModal
        isOpen={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        title="Clone Process"
        size="md"
      >
        <StandardModal.Body>
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Create a copy of <strong className="text-gray-900 dark:text-gray-100">{processToClone?.name}</strong>
            </p>
            <StandardInput
              label="New Process Name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Enter name for cloned process"
              required
            />
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setCloneModalOpen(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleConfirmClone}
            disabled={!cloneName.trim()}
            leftIcon={<Copy size={16} />}
          >
            Clone Process
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
};

export default ProcessBuilderList;
